import {
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
  Vector2,
} from 'three';

import type { Store } from 'src/app/store';
import { getSelectPoint } from 'src/state';
import { addToRenderLoop } from './renderLoop';

function degreesToRadians(degrees: number): number {
  return Math.PI * degrees / 180;
}

const yAxis = new Vector3(0, 1, 0);
const slowSpeed = 50; // speed in units per second
const fastSpeed = slowSpeed * 5; // speed in units per second

const angleOffset = degreesToRadians(25);
const material = new MeshBasicMaterial({ color: 0x333333 });
const geometry = new SphereGeometry( 8, 32, 16 );

const sixtyDegrees = degreesToRadians(60)
const threeHundredDegrees = degreesToRadians(300)
const minSlowAngle = degreesToRadians(0);
const maxSlowAngle = sixtyDegrees;

// return an angle in radians
function placePlanet(placement: number): number {
  const slowSection = 2 * Math.min(placement, 0.5) * sixtyDegrees;
  const fastSection = 2 * Math.max(placement - 0.5, 0) * threeHundredDegrees;
  return slowSection + fastSection + angleOffset;
}

function getUpdatePointFunction(obj: Object3D, radius: number) {
  return (delta: number) => {
    // current angle
    const angle = (Math.PI * 2) - (new Vector2(obj.position.x, obj.position.z).angle()) - angleOffset;

    let distance;
    if (minSlowAngle < angle && angle <= maxSlowAngle) {
      // distance = delta * slowSpeed;

      const radiansToSpeedChange = maxSlowAngle - angle;
      const timeUntilSpeedChange = radiansToSpeedChange * radius / slowSpeed;

      if (delta < timeUntilSpeedChange) {
        distance = delta * slowSpeed;
      } else {
        distance = (timeUntilSpeedChange * slowSpeed) + 
          ((delta - timeUntilSpeedChange) * fastSpeed);
      }
    } else {
      const radiansToSpeedChange = (2 * Math.PI) - angle;
      const timeUntilSpeedChange = radiansToSpeedChange * radius / fastSpeed;

      if (delta < timeUntilSpeedChange) {
        distance = delta * fastSpeed
      } else {
        distance = (timeUntilSpeedChange * fastSpeed) + 
          ((delta - timeUntilSpeedChange) * slowSpeed);
      }
    }

    // the angle to rotate
    const theta = distance / radius;
    obj.position.applyAxisAngle(yAxis, theta);
  };
}

// placement ranging 0 - 1
export function drawPoint(pointId: string, placement: number, radius: number, store: Store): Mesh {
  const point = getSelectPoint(pointId)(store.getState());
  const sphere = new Mesh( geometry, material.clone() );
  sphere.name = point.fields.name['en-US'];

  // place the point on the orbit
  const theta = placePlanet(placement);
  const position = new Vector3(radius, 0, 0);
  position.applyAxisAngle(yAxis, theta);
  sphere.position.set(position.x, position.y, position.z);

  addToRenderLoop(getUpdatePointFunction(sphere, radius));
  return sphere;
}
